/* ─── Viewport ─── */

.viewport {
  position: fixed;
  bottom: var(--space-4);
  left: 50%;
  transform: translateX(-50%);
  z-index: var(--z-toast);
  display: flex;
  flex-direction: column-reverse;
  gap: var(--space-2);
  pointer-events: none;
  max-width: calc(100vw - 2 * var(--space-4));
}

/* ─── Root ─── */

.root {
  background: var(--md-inverse-surface);
  color: var(--md-inverse-on-surface);
  border-radius: var(--shape-sm);
  box-shadow: var(--elevation-3);
  min-width: 288px;
  max-width: 560px;
  padding: var(--space-3) var(--space-4);
  display: flex;
  align-items: center;
  gap: var(--space-3);
  pointer-events: auto;
  font-family: var(--font-sans);
  transition: opacity var(--motion-duration-medium1)
      var(--motion-easing-standard),
    transform var(--motion-duration-medium1) var(--motion-easing-standard);
}

.root[data-starting-style],
.root[data-ending-style] {
  opacity: 0;
  transform: translateY(1rem);
}

.root[data-swipe] {
  transition: none;
  /* Base UI sets these mid-gesture. Without the fallbacks an unset one makes the
     whole transform invalid, not just its own axis. */
  transform: translateX(var(--toast-swipe-movement-x, 0px))
    translateY(var(--toast-swipe-movement-y, 0px));
}

.root[data-limited] {
  opacity: 0;
  transform: scale(0.95);
}

/* ─── Type variants ─── */

.root[data-type="error"] {
  background: var(--status-critical-container);
  color: var(--md-on-surface);
}

.root[data-type="success"] {
  background: var(--status-success-container);
  color: var(--md-on-surface);
}

/* ─── Content ─── */

.content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

/* ─── Title ─── */

.title {
  font: var(--type-title-sm);
  letter-spacing: var(--tracking-title-sm);
  font-family: var(--font-sans);
  margin: 0;
}

/* ─── Description ─── */

.description {
  font: var(--type-body-lg);
  letter-spacing: var(--tracking-body-lg);
  font-family: var(--font-sans);
  margin: 0;
  opacity: 0.8;
}

/* ─── Action ─── */

.action {
  font: var(--type-label-lg);
  letter-spacing: var(--tracking-label-lg);
  font-family: var(--font-sans);
  color: var(--md-inverse-primary);
  background: transparent;
  border: none;
  padding: var(--space-1) var(--space-2);
  border-radius: var(--shape-xs);
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  position: relative;
}

.action:focus-visible {
  --focus-ring-color: var(--md-inverse-primary);
}

/* The error and success toasts swap the dark surface for a light container, so
   everything below re-points what the default styling aims at a dark one. */
.root[data-type="error"] .action,
.root[data-type="success"] .action {
  color: var(--md-primary);
}

.root[data-type="error"] .action:focus-visible,
.root[data-type="success"] .action:focus-visible,
.root[data-type="error"] .close:focus-visible,
.root[data-type="success"] .close:focus-visible {
  --focus-ring-color: var(--md-primary);
}

.root[data-type="error"] .close,
.root[data-type="success"] .close {
  color: var(--md-on-surface);
}

/* ─── Close ─── */

.close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* Keeps the dismiss control hard right even when the toast has no
     .content wrapper to absorb the free space. */
  margin-inline-start: auto;
  width: 2.75rem;
  height: 2.75rem;
  padding: 0;
  border: none;
  border-radius: var(--shape-full);
  background: transparent;
  color: var(--md-inverse-on-surface);
  cursor: pointer;
  flex-shrink: 0;
  position: relative;
}

.action::before,
.close::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: currentColor;
  opacity: 0;
  transition: opacity var(--motion-duration-short3);
  pointer-events: none;
}

@media (hover: hover) {
  .action:hover::before,
  .close:hover::before {
    opacity: var(--state-hover);
  }
}

.action:focus-visible::before,
.close:focus-visible::before {
  opacity: var(--state-focus);
}

.action:active::before,
.close:active::before {
  opacity: var(--state-pressed);
}

.close:focus-visible {
  --focus-ring-color: var(--md-inverse-primary);
}

.close svg {
  width: 1rem;
  height: 1rem;
}
